import { pick } from 'lodash';
import { Loader, Pagination } from '@creditsights/styleguide-web';
import { AgGridEvent, GridApi, GridReadyEvent, SortChangedEvent, ColumnApi, GetRowIdFunc } from 'ag-grid-community';
import classNames from 'classnames/bind';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { createSearchParams, useNavigate, useSearchParams } from 'react-router-dom';
import { EXTERNAL_ROUTES } from '../../Base.constants';
import { useFetchIssuers } from '../../hooks/issuers';
import useFetchUserPermissions from '../../hooks/permissions/useFetchUserPermissions';
import { IssuerListItemDto } from '../../services/dtos/issuer/issuerList.dto';
import { hasPermissionToRP } from '../../utils/permissionsUtils';
import { additionalDefaultColDef, COLUMN_PERSISTENCE_KEY, COMPANY_COLUMNS } from './Company.constants';
import Table from '../../components/FullPageTable';

import styles from './Companies.module.scss';

const cx = classNames.bind(styles);

const CompanyTable = Table<IssuerListItemDto>;

const Companies = (): React.ReactElement => {
  const [searchParams, setSearchParams] = useSearchParams();
  const gridApiRef = useRef<GridApi | null>(null);
  const columnApiRef = useRef<ColumnApi | null>(null);
  const sort = useMemo(
    () => ({
      sortField: searchParams.get('sortField') ?? 'name',
      sortOrderAsc: !(searchParams.get('sortOrder') === 'desc'),
    }),
    [searchParams]
  );

  const navigate = useNavigate();
  const { data: userPermissions } = useFetchUserPermissions();

  useEffect(() => {
    if (!hasPermissionToRP(userPermissions)) {
      navigate(EXTERNAL_ROUTES.companyList);
    }
  }, [userPermissions]);

  const page = useMemo(() => {
    const page = Number(searchParams.get('page') ?? 1);
    if (page > 0) {
      return page;
    }
    return 1;
  }, [searchParams]);

  const { data, isLoading, isFetching } = useFetchIssuers({
    skip: (page - 1) * 100,
    take: 100,
    ...sort,
  });

  const companies = data?.results;

  const onSortChanged = useCallback(
    (event: SortChangedEvent) => {
      const column = event.columnApi.getColumnState().find((c) => c.sort);
      const params = createSearchParams(searchParams);
      if (!column) {
        params.set('sortField', 'name');
        params.set('sortOrder', 'asc');
      } else {
        params.set('sortField', column.colId);
        params.set('sortOrder', column.sort || 'asc');
      }
      setSearchParams(params, { replace: true });
    },
    [searchParams]
  );

  const onPageChanged = (page) => {
    const params = createSearchParams(searchParams);
    params.set('page', page);
    setSearchParams(params, { replace: true });
  };

  const loadColumns = (): boolean => {
    const columnStateString = localStorage.getItem(COLUMN_PERSISTENCE_KEY);
    if (!columnStateString) {
      return false;
    }
    try {
      const columnState = JSON.parse(columnStateString);
      columnApiRef.current?.applyColumnState({
        state: columnState,
        applyOrder: true,
        defaultState: { pinned: null, sort: null, sortIndex: null },
      });
    } catch {
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (isLoading || isFetching) {
      // Not sure why timeouts are needed, but it something that I found in ag-grid github issue tracker
      // https://github.com/ag-grid/ag-grid/issues/3849#issuecomment-1474747809
      setTimeout(() => gridApiRef.current?.showLoadingOverlay());
    } else if (companies && companies.length === 0) {
      setTimeout(() => gridApiRef.current?.showNoRowsOverlay());
    } else {
      gridApiRef.current?.hideOverlay();
    }
  }, [isLoading, isFetching, companies]);

  const onGridReady = useCallback((event: GridReadyEvent) => {
    gridApiRef.current = event.api;
    columnApiRef.current = event.columnApi;
    if (!loadColumns()) {
      resizeColumnsToFit();
    }
    event.columnApi.applyColumnState({ state: [{ colId: sort.sortField, sort: sort.sortOrderAsc ? 'asc' : 'desc' }] });
  }, []);

  const resizeColumnsToFit = useCallback(() => {
    gridApiRef.current?.sizeColumnsToFit();
  }, []);

  useEffect(() => {
    gridApiRef.current?.ensureIndexVisible(0, 'top');
  }, [page]);

  const renderPagination = () => (
    <Pagination
      activePage={page}
      onPageChange={onPageChanged}
      totalPages={Math.floor((data?.totalResults ?? 0) / 100 + 1)}
    />
  );

  const persistColumnState = useCallback((event: AgGridEvent) => {
    const columnState = event.columnApi.getColumnState();
    const adjustedState = columnState.map((state) => pick(state, ['colId', 'pinned', 'width', 'flex']));
    localStorage.setItem(COLUMN_PERSISTENCE_KEY, JSON.stringify(adjustedState));
  }, []);

  const renderHeader = () => (
    <h2 className={cx('header')}>
      Results<span>{data?.totalResults}</span>
    </h2>
  );

  useEffect(() => {
    const storageEventHandler = (event: StorageEvent) => {
      if (event.key === COLUMN_PERSISTENCE_KEY) {
        loadColumns();
      }
    };
    window.addEventListener('storage', storageEventHandler);
    return () => {
      window.removeEventListener('storage', storageEventHandler);
    };
  }, []);

  const rowIdResolver = useCallback<GetRowIdFunc<IssuerListItemDto>>((params) => params.data.tagId, []);

  return (
    <div className={cx('body')}>
      <CompanyTable
        pageTitle="Companies"
        headerComponent={renderHeader()}
        paginationComponent={renderPagination()}
        onColumnMoved={persistColumnState}
        onColumnPinned={persistColumnState}
        onColumnResized={persistColumnState}
        onGridReady={onGridReady}
        rowData={companies}
        getRowId={rowIdResolver}
        columnDefs={COMPANY_COLUMNS}
        onSortChanged={onSortChanged}
        rowSelection="multiple"
        suppressRowVirtualisation
        suppressRowClickSelection
        loadingOverlayComponent={Loader}
        overlayNoRowsTemplate="No results found"
        additionalDefaultColDef={additionalDefaultColDef}
      />
    </div>
  );
};

export default Companies;
